import React, { useState, useEffect } from "react";
import {
  accountInfoWidth,
  darkModeBorder,
  darkModeWidgetBackground,
  darkModeWidgetBorderColor,
  lightModeBorder,
  lightModeWidgetBackground,
  lightModeWidgetBorderColor,
  primaryBlue,
  stockInfoWidth,
} from "../util/style";
import { useAppContext } from "./AppContext";
import MarketStatus from "./MarketStatus";
import RefreshStatus from "./RefreshStatus";
import TickerPriceTag from "./TickerPriceTag";
import UpDownArrow from "./UpDownArrow";

const HeaderStatus: React.FC = () => {
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const {
    allAccountString,
    darkMode,
    getFont,
    selectedTicker,
    selectedAccount,
    selectedCurrency,
    accountInfo,
    lastPrice,
    displayDataParsed,
    holdingDayChange,
    holdingMarketValue,
    lastClosePriceDelta,
    currencyMultiplier,
    selectedTickerCurrency,
    privateMode,
    adminMode,
    numFormat,
  } = useAppContext() || {};

  return (
    <div
      style={{
        display: "flex",
        margin: "10px 10px 10px 10px",
        background: darkMode
          ? darkModeWidgetBackground
          : lightModeWidgetBackground,
        justifyContent: "space-between",
        padding: "10px 10px",
        flex: "0 0 auto",
        borderRadius: "2px",
        overflow: isMobile ? "auto" : "hidden",
        overflowX: isMobile ? "auto" : "hidden",
        overflowY: "hidden",
        border: `1px solid ${
          darkMode ? darkModeWidgetBorderColor : lightModeWidgetBorderColor
        }`,
        fontSize: getFont?.(isMobile ? 10 : 12),
        transition: "all 0.25s ease",
        flexWrap: "nowrap",
        whiteSpace: isMobile ? "nowrap" : "normal",
      }}
      className="hoverShadow"
    >
      {/* Left side */}
      {selectedTicker !== "ALL" && (
        <div style={{ display: "flex", flexShrink: 0 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                minWidth: stockInfoWidth + "px",
              }}
            >
              <TickerPriceTag />
            </div>
            <div
              style={{
                width: "100%",
                borderTop: `1px solid ${
                  darkMode ? darkModeBorder : lightModeBorder
                }`,
                marginTop: "0px",
              }}
            >
              {
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div>Day&nbsp;Change&nbsp;</div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <UpDownArrow
                      number={
                        (displayDataParsed?.accounts?.[selectedAccount]
                          ?.trades?.[selectedTicker]?.resultingShareCount || 0) *
                        (lastClosePriceDelta || 0)
                      }
                    />
                    <div style={{ marginLeft: "2px" }}>
                      $
                      {numFormat?.(
                        Math.abs(
                          displayDataParsed?.accounts?.[selectedAccount]
                            ?.trades?.[selectedTicker]?.resultingShareCount ===
                            0
                            ? 0
                            : (displayDataParsed?.accounts?.[selectedAccount]
                                ?.trades?.[selectedTicker]
                                ?.resultingShareCount || 0) *
                                (lastClosePriceDelta || 0) *
                                (currencyMultiplier || 1)
                        ),
                        true
                      ) +
                        " " +
                        selectedCurrency}
                    </div>
                  </div>
                </div>
              }
            </div>
            <div
              style={{
                width: "100%",
                borderTop: `1px solid ${
                  darkMode ? darkModeBorder : lightModeBorder
                }`,
              }}
            >
              {
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div>Holding&nbsp;</div>
                  <div>
                    {numFormat?.(
                      displayDataParsed?.accounts?.[selectedAccount]?.trades?.[
                        selectedTicker
                      ]?.resultingShareCount || 0,
                      true,
                      0
                    )}
                    &nbsp;Share
                  </div>
                </div>
              }
            </div>
            <div style={{ width: "100%" }}>
              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <div>Cost&nbsp;Basis&nbsp;</div>
                <div>
                  $
                  {numFormat?.(
                    (lastPrice?.[selectedTicker] || 0) -
                      (displayDataParsed?.accounts?.[selectedAccount]?.trades?.[
                        selectedTicker
                      ]?.unrealizedPnL || 0) /
                        (displayDataParsed?.accounts?.[selectedAccount]
                          ?.trades?.[selectedTicker]?.resultingShareCount || 1),
                    true,
                    2
                  ) +
                    " " +
                    selectedTickerCurrency}
                </div>
              </div>
            </div>
            <div style={{ width: "100%" }}>
              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <div>Market&nbsp;Value&nbsp;</div>
                <div>
                  $
                  {numFormat?.(
                    (lastPrice?.[selectedTicker] || 0) *
                      (displayDataParsed?.accounts?.[selectedAccount]?.trades?.[
                        selectedTicker
                      ]?.resultingShareCount || 0) *
                      (currencyMultiplier || 1),
                    true
                  ) +
                    " " +
                    selectedCurrency}
                </div>
              </div>
            </div>

            <div
              style={{
                width: "100%",
                borderTop: `1px solid ${
                  darkMode ? darkModeBorder : lightModeBorder
                }`,
              }}
            >
              {
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div style={{}}>Unrealized&nbsp;</div>
                  <div>
                    $
                    {numFormat?.(
                      (displayDataParsed?.accounts?.[selectedAccount]?.trades?.[
                        selectedTicker
                      ]?.unrealizedPnL || 0) * (currencyMultiplier || 1),
                      true
                    ) +
                      " " +
                      selectedCurrency}
                  </div>
                </div>
              }
            </div>
            <div
              style={{
                width: "100%",
              }}
            >
              {
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div>Realized&nbsp;</div>
                  <div>
                    $
                    {numFormat?.(
                      (displayDataParsed?.accounts?.[selectedAccount]?.trades?.[
                        selectedTicker
                      ]?.realizedPnL || 0) * (currencyMultiplier || 1),
                      true
                    ) +
                      " " +
                      selectedCurrency}
                  </div>
                </div>
              }
            </div>
            <div
              style={{
                width: "100%",
                borderTop: `1px solid ${
                  darkMode ? darkModeBorder : lightModeBorder
                }`,
              }}
            >
              {
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div style={{}}>Cumulative&nbsp;Gain&nbsp;</div>
                  <div>
                    $
                    {numFormat?.(
                      (displayDataParsed?.accounts?.[selectedAccount]?.trades?.[
                        selectedTicker
                      ]?.overallPnL || 0) * (currencyMultiplier || 1),
                      true
                    ) +
                      " " +
                      selectedCurrency}
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      )}

      <div style={{ marginLeft: "5px", marginRight: "auto", flexShrink: 0 }}>
        <MarketStatus />
        <RefreshStatus />
      </div>

      {/* Right side */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          minWidth: accountInfoWidth + "px",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            borderBottom: `1px solid ${
              darkMode ? darkModeBorder : lightModeBorder
            }`,
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>Ticker&nbsp;&nbsp;</div>
          <div
            style={{
              background:
                selectedAccount === allAccountString
                  ? darkMode
                    ? "rgb(80,80,80)"
                    : "rgb(150,150,150)"
                  : primaryBlue,
              color: "white",
              marginBottom: "1px",
              padding: "0px 3px 0px 4px",
              fontWeight: "600",
              borderRadius: "2px",
            }}
          >
            {(accountInfo as Record<string, { name?: string }>)?.[selectedAccount]?.name &&
              (accountInfo as Record<string, { name?: string }>)?.[selectedAccount]?.name + " "}
            {selectedAccount}
          </div>
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            borderBottom: `1px solid ${
              darkMode ? darkModeBorder : lightModeBorder
            }`,
          }}
        >
          <div>Day&nbsp;Change&nbsp;</div>
          <div>
            {(adminMode || privateMode) && (
              <UpDownArrow number={holdingDayChange || 0} />
            )}
            <span style={{ marginLeft: "2px" }}>
              $
              {numFormat?.(Math.abs((holdingDayChange || 0) * (currencyMultiplier || 1)), true)}
              &nbsp;{selectedCurrency}
            </span>
          </div>
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            borderBottom: `1px solid ${
              darkMode ? darkModeBorder : lightModeBorder
            }`,
          }}
        >
          <div>Market&nbsp;Value&nbsp;</div>
          <div>
            $
            {numFormat?.(Math.abs((holdingMarketValue || 0) * (currencyMultiplier || 1)), true)}
            &nbsp;{selectedCurrency}
          </div>
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>Unrealized&nbsp;</div>$
          {numFormat?.(
            (displayDataParsed?.accounts?.[selectedAccount]?.unrealizedPnL || 0) *
              (currencyMultiplier || 1),
            true
          )}
          &nbsp;{selectedCurrency}
        </div>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>Realized&nbsp;</div>$
          {numFormat?.(
            (displayDataParsed?.accounts?.[selectedAccount]?.realizedPnL || 0) *
              (currencyMultiplier || 1),
            true
          )}
          &nbsp;{selectedCurrency}
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            borderTop: `1px solid ${
              darkMode ? darkModeBorder : lightModeBorder
            }`,
          }}
        >
          <div>Cumulative&nbsp;Gain&nbsp;</div>$
          {numFormat?.(
            (displayDataParsed?.accounts?.[selectedAccount]?.overallPnL || 0) *
              (currencyMultiplier || 1),
            true
          )}
          &nbsp;{selectedCurrency}
        </div>
      </div>
    </div>
  );
};

export default HeaderStatus;
